import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import ValidationStatus, {
  ValidationStatusContainer,
} from '@databases/form/ValidationStatus';
import {
  InlineEditContainer,
  InlineEditStateless,
} from '@databases/inline-edit/InlineEdit';
import InlineEditWrapper from '@databases/inline-edit/InlineEditWrapper';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { defaultStoreUtils } from '@databases/redux';
import { useSkeletonEnabled } from '@databases/skeleton';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';

import DatabaseNameEdit, {
  DATABASE_NAME_MAX_LENGTH,
  DatabaseNameEditContainer,
} from './DatabaseNameEdit';
import DatabaseNameView from './DatabaseNameView';

export const StyledInlineEditWrapper = styled(InlineEditWrapper)<{
  isEditing?: boolean;
}>`
  margin-left: ${token('space.100')};
  width: 99%;
  margin-bottom: ${token('space.negative.200')};

  > ${ValidationStatusContainer} > ${InlineEditContainer} {
    border-radius: 3px;
  }

  > ${ValidationStatusContainer}
    > ${InlineEditContainer}
    > div
    > ${ValidationStatusContainer}
    > ${DatabaseNameEditContainer}
    > div {
    border: none;
    input {
      font: ${token('font.heading.large')};
      padding: ${token('space.025')} ${token('space.050', '4px')};
    }
  }
`;

export interface DatabaseNameInlineEditProps {
  contentId: string;
  isReadonly?: boolean;
}

/**
 * Renders an inline edit field to edit the database name.
 * When hitting the enter button or on blur the input field, the database draft with the changed name is automatically saved.
 */
const DatabaseNameInlineEdit: FunctionComponent<DatabaseNameInlineEditProps> = ({
  contentId,
  isReadonly,
}) => {
  const client = useAnalytics();
  const { formatMessage } = useIntl();

  const [isEditing, setIsEditing] = useState(false);

  const value = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    return database.name;
  });

  const [databaseName, setDatabaseName] = useState(value);
  const [isValidName, setIsValidName] = useState(true);

  const handleSubmit = useCallback(
    async (value: string) => {
      if (isValidName) {
        try {
          await getConfluenceService().updateContentTitle(contentId, value);
          client.sendEmbedDatabaseTitleUpdatedEvent({
            contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        } catch (possibleError) {
          const error =
            possibleError instanceof Error
              ? possibleError
              : new Error(String(possibleError));
          logError('Error updating native database title', error);
          throw error;
        }
      }
      setIsEditing(false);
    },
    [client, contentId, isValidName]
  );

  const onChange = useCallback((value: string, isValid?: boolean) => {
    setDatabaseName(value);
    setIsValidName(isValid!);
  }, []);

  const canEdit = !isReadonly;
  const isSkeletonEnabled = useSkeletonEnabled();

  const readView = useCallback(
    () => <DatabaseNameView name={databaseName} />,
    [databaseName]
  );

  const editView = useCallback(
    () => (
      <DatabaseNameEdit onChange={onChange} initialValue={databaseName} showPlaceholder />
    ),
    [onChange, databaseName]
  );

  return (
    <StyledInlineEditWrapper
      hideBorderOnFocus
      isDraggable={false}
      isEditing={isEditing}
      isInvalid={canEdit && !isValidName}
    >
      {!canEdit ? (
        <DatabaseNameView name={value} />
      ) : (
        <ValidationStatus
          error={
            isValidName
              ? undefined
              : formatMessage(messages['validation.database-name-length'], {
                  maxLength: DATABASE_NAME_MAX_LENGTH,
                })
          }
        >
          {() => (
            <InlineEditStateless<string>
              isReadOnly={!canEdit}
              readView={readView}
              editView={editView}
              isEditing={isEditing && !isSkeletonEnabled}
              onIsEditingChange={setIsEditing}
              value={databaseName}
              onReset={handleSubmit}
              onBlur={handleSubmit} // clicking 'enter' triggers onBlur
              preserveEditWidth
            />
          )}
        </ValidationStatus>
      )}
    </StyledInlineEditWrapper>
  );
};

export default DatabaseNameInlineEdit;
